import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { api } from '../../services/api';

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;

// Event type to icon mapping (Requirement 4.3)
const EVENT_ICONS = {
  shot: '🏀',
  three: '🎯',
  dunk: '💥',
  rebound: '📊',
  foul: '⚠️',
  sub: '🔄',
  substitution: '🔄',
  timeout: '⏸️',
  turnover: '❌',
  steal: '🖐️',
  block: '🚫',
  assist: '🎯',
  freethrow: '🎪',
  free_throw: '🎪',
  jumpball: '⬆️',
  jump_ball: '⬆️',
  default: '📝',
};

/**
 * Get icon for event type (Requirement 4.3)
 * @param {string} eventType - The type of event
 * @returns {string} - The corresponding emoji icon
 */
export function getEventIcon(eventType) {
  if (!eventType) return EVENT_ICONS.default;
  const normalizedType = eventType.toLowerCase().replace(/[_\s-]/g, '');
  
  // Check for specific keywords in the event type
  if (normalizedType.includes('three') || normalizedType.includes('3pt')) return EVENT_ICONS.three;
  if (normalizedType.includes('dunk')) return EVENT_ICONS.dunk;
  if (normalizedType.includes('rebound')) return EVENT_ICONS.rebound;
  if (normalizedType.includes('foul')) return EVENT_ICONS.foul;
  if (normalizedType.includes('sub')) return EVENT_ICONS.sub;
  if (normalizedType.includes('timeout')) return EVENT_ICONS.timeout;
  if (normalizedType.includes('turnover')) return EVENT_ICONS.turnover;
  if (normalizedType.includes('steal')) return EVENT_ICONS.steal;
  if (normalizedType.includes('block')) return EVENT_ICONS.block;
  if (normalizedType.includes('assist')) return EVENT_ICONS.assist;
  if (normalizedType.includes('freethrow') || normalizedType.includes('free')) return EVENT_ICONS.freethrow;
  if (normalizedType.includes('jump')) return EVENT_ICONS.jumpball;
  if (normalizedType.includes('shot') || normalizedType.includes('made') || normalizedType.includes('miss')) return EVENT_ICONS.shot;
  
  return EVENT_ICONS[eventType.toLowerCase()] || EVENT_ICONS.default;
}

/**
 * Sort events in reverse chronological order (newest first) (Requirement 4.1, 5.1)
 * For Hupu data: preserve original order since API returns events in correct reverse chronological order
 * For ESPN data: sort by quarter (desc) then by time (asc, lower time = more recent)
 * @param {Array} events - Array of play-by-play events
 * @returns {Array} - Sorted events array
 */
export function sortEventsReverseChronological(events) {
  if (!events || !Array.isArray(events)) return [];
  
  // Check if we should preserve original order (for Hupu data)
  const shouldPreserveOrder = events.length > 0 && events[0]?._preserveOrder;
  if (shouldPreserveOrder) {
    // Hupu API already returns events in reverse chronological order
    // The period field may be incorrect for some events, so preserve original order
    return [...events];
  }
  
  return [...events].sort((a, b) => {
    // First compare by quarter (higher quarter = more recent)
    const quarterA = a.quarter || 0;
    const quarterB = b.quarter || 0;
    if (quarterB !== quarterA) return quarterB - quarterA;
    
    // Then compare by time within quarter (lower time = more recent in basketball)
    // Parse time strings like "10:23" or "Q1 10:23"
    const parseTime = (timeStr) => {
      if (!timeStr) return 0;
      const match = timeStr.match(/(\d+):(\d+)/);
      if (match) {
        return parseInt(match[1]) * 60 + parseInt(match[2]);
      }
      return 0;
    };
    
    const timeA = parseTime(a.time);
    const timeB = parseTime(b.time);
    
    // Lower time value means more recent (closer to end of quarter)
    return timeA - timeB;
  });
}

/**
 * Filter events by quarter (Requirement 4.5)
 * @param {Array} events - Array of play-by-play events
 * @param {number|null} quarter - Quarter to filter by (null for all)
 * @returns {Array} - Filtered events array
 */
export function filterEventsByQuarter(events, quarter) {
  if (!events || !Array.isArray(events)) return [];
  if (quarter === null || quarter === undefined || quarter === 0) return events;
  
  // Filter by quarter/period field
  return events.filter(event => {
    const eventQuarter = event.quarter || event.period || 0;
    return eventQuarter === quarter;
  });
}

/**
 * PlayByPlayPanel - Displays play-by-play events with source switching and quarter filtering
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 5.1, 5.2, 5.3, 5.4
 * 
 * @param {Object} props
 * @param {string} props.teamA - Away team name
 * @param {string} props.teamB - Home team name
 * @param {string} props.gameId - Game ID for ESPN data (from balldontlie/ESPN)
 * @param {boolean} props.isLive - Whether the game is currently live
 */
export function PlayByPlayPanel({ teamA, teamB, gameId, isLive }) {
  const { t, i18n } = useTranslation();
  const getDefaultSource = (language) =>
    (language || '').toLowerCase().startsWith('zh') ? 'hupu' : 'espn';
  const [dataSource, setDataSource] = useState(() => getDefaultSource(i18n.language)); // 'hupu' or 'espn' (Requirement 3.1)
  const [quarterFilter, setQuarterFilter] = useState(0); // 0 = all, 1-4 = specific quarter (Requirement 4.4)
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const isInitialLoadRef = useRef(true); // Track if this is the initial load

  // Fetch play-by-play data based on selected source
  const fetchPlayByPlay = useCallback(async () => {
    if (!teamA || !teamB) return;
    
    try {
      // Only show loading on initial load, not during polling
      if (isInitialLoadRef.current) {
        setLoading(true);
      }
      let data;
      let rawEvents = [];
      let preserveOrder = false; // Flag to preserve original order from API
      
      if (dataSource === 'hupu') {
        // Fetch from Hupu API (Requirement 3.2, 4.1-4.7)
        data = await api.getHupuPlayByPlay(teamA, teamB);
        // Handle Hupu API response format
        if (data?.events && Array.isArray(data.events)) {
          rawEvents = data.events;
        } else if (data?.liveMessages && Array.isArray(data.liveMessages)) {
          rawEvents = data.liveMessages;
        } else if (Array.isArray(data)) {
          rawEvents = data;
        }
        // Hupu API returns events in reverse chronological order already
        // and the period field is unreliable, so preserve original order
        preserveOrder = true;
      } else {
        // Fetch from ESPN play-by-play API (local handler)
        data = await api.getEspnPlayByPlay(gameId);
        if (data?.events && Array.isArray(data.events)) {
          rawEvents = data.events;
        } else if (data?.playByPlay && Array.isArray(data.playByPlay)) {
          // Fallback to old format from game-detail API
          rawEvents = data.playByPlay.map(e => ({
            ...e,
            description: e.text || e.description || '',
            quarter: e.period || e.quarter || 0,
            time: e.clock || e.time || '',
            homeScore: e.scoreHome,
            awayScore: e.scoreAway,
            eventType: e.typeText || e.type || '',
          }));
        }
      }
      
      // Transform events to normalize field names
      const transformEvent = (e, index) => ({
        ...e,
        description: e.description || e.event || e.text || e.message || '',
        quarter: e.quarter || e.period || e.q || 0,
        eventType: e.eventType || e.type || '',
        _originalIndex: index, // Preserve original order for Hupu
        _preserveOrder: preserveOrder,
      });
      
      setEvents(rawEvents.map(transformEvent));
      
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch play-by-play:', err);
      setError(err.message || t('live.dataUnavailable'));
      // Don't clear events on polling error, keep showing old data
      if (isInitialLoadRef.current) {
        setEvents([]);
      }
    } finally {
      if (isInitialLoadRef.current) {
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    }
  }, [teamA, teamB, gameId, dataSource, t]);

  // Initial fetch and polling setup
  useEffect(() => {
    fetchPlayByPlay();

    // Set up polling every 30 seconds when live
    if (isLive) {
      const intervalId = setInterval(fetchPlayByPlay, POLLING_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [fetchPlayByPlay, isLive]);

  // Remember source preference during session (Requirement 3.4)
  useEffect(() => {
    const savedSource = sessionStorage.getItem('playByPlaySource');
    if (savedSource && (savedSource === 'hupu' || savedSource === 'espn')) {
      setDataSource(savedSource);
    }
  }, []);

  const handleSourceChange = (source) => {
    setDataSource(source);
    sessionStorage.setItem('playByPlaySource', source);
    setQuarterFilter(0); // Reset quarter filter when changing source
    isInitialLoadRef.current = true; // Reset initial load flag to show loading
    setLoading(true); // Show loading when switching source
  };

  // Process and filter events
  const processedEvents = useMemo(() => {
    // Sort events in reverse chronological order (Requirement 4.1, 5.1)
    const sorted = sortEventsReverseChronological(events);
    // Filter by quarter if selected (Requirement 4.5)
    return filterEventsByQuarter(sorted, quarterFilter);
  }, [events, quarterFilter]);

  // Get unique quarters from events for filter buttons
  const availableQuarters = useMemo(() => {
    const quarters = new Set(events.map(e => e.quarter).filter(q => q));
    return Array.from(quarters).sort((a, b) => a - b);
  }, [events]);

  // Quarter filter labels
  const getQuarterLabel = (quarter) => {
    if (quarter === 0) return t('playByPlay.all', '全部');
    if ((i18n.language || '').toLowerCase().startsWith('zh')) {
      return `第${quarter}节`;
    }
    return `Q${quarter}`;
  };

  // Format timestamp for display (Requirement 3.5)
  const formatLastUpdate = (date) => {
    if (!date) return '';
    const locale = (i18n.language || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Card className="p-6">
      {/* Header with source toggle and live indicator */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">📝</span>
          <h3 className="font-semibold text-accent">
            {t('playByPlay.title', '文字直播')}
          </h3>
          {/* Live indicator (Requirement 4.7) */}
          {isLive && (
            <Badge variant="positive" className="animate-pulse ml-2">
              🔴 {t('playByPlay.liveIndicator', '直播中')}
            </Badge>
          )}
        </div>
        
        {/* Source toggle buttons (Requirement 3.1) */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSourceChange('hupu')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              dataSource === 'hupu'
                ? 'bg-accent-warm text-white'
                : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
            }`}
          >
            🇨🇳 {t('playByPlay.hupuSource', '虎扑中文')}
          </button>
          <button
            onClick={() => handleSourceChange('espn')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              dataSource === 'espn'
                ? 'bg-accent-strong text-white'
                : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
            }`}
          >
            🇺🇸 {t('playByPlay.espnSource', 'ESPN English')}
          </button>
        </div>
      </div>

      {/* Quarter filter buttons (Requirement 4.4, 5.3) */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setQuarterFilter(0)}
          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
            quarterFilter === 0
              ? 'bg-accent-strong text-white'
              : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
          }`}
        >
          {getQuarterLabel(0)}
        </button>
        {[1, 2, 3, 4].map((q) => (
          <button
            key={q}
            onClick={() => setQuarterFilter(q)}
            disabled={!availableQuarters.includes(q)}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              quarterFilter === q
                ? 'bg-accent-strong text-white'
                : availableQuarters.includes(q)
                  ? 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
                  : 'bg-surface/80 text-gray-600 cursor-not-allowed'
            }`}
          >
            {getQuarterLabel(q)}
          </button>
        ))}
      </div>

      {/* Event count and source attribution (Requirement 4.6, 5.4) */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
        <span>
          {t('playByPlay.eventCount', '共 {{count}} 条事件', { count: processedEvents.length })}
          {' · '}
          {t('playByPlay.dataSource', '数据来源')}: {dataSource === 'hupu'
            ? t('playByPlay.hupu', 'Hupu')
            : t('playByPlay.espn', 'ESPN')}
        </span>
        {/* Last update timestamp (Requirement 3.5) */}
        {lastUpdate && (
          <span>
            {t('live.lastUpdate')}: {formatLastUpdate(lastUpdate)}
          </span>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center h-48">
          <div className="animate-pulse text-gray-400">{t('common.loading')}</div>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="text-center py-8 text-gray-400">
          {t('playByPlay.dataUnavailable', '文字直播暂时不可用')}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && processedEvents.length === 0 && (
        <div className="text-center py-8">
          <div className="text-4xl mb-3">📝</div>
          <div className="text-gray-400">
            {t('playByPlay.noEvents', '暂无文字直播数据')}
          </div>
          {!isLive && (
            <div className="text-sm text-gray-500 mt-2">
              {t('playByPlay.notStartedHint', '比赛开始后将显示文字直播')}
            </div>
          )}
        </div>
      )}

      {/* Events list (Requirement 4.2, 5.2) */}
      {!loading && !error && processedEvents.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {processedEvents.map((event, index) => (
            <PlayByPlayEvent key={index} event={event} dataSource={dataSource} />
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * Individual play-by-play event component
 * Displays time, score, icon, and description (Requirement 4.2, 5.2)
 */
function PlayByPlayEvent({ event, dataSource }) {
  const { time, quarter, homeScore, awayScore, description, eventType, team, player } = event;
  
  // Get icon for event type (Requirement 4.3)
  const icon = getEventIcon(eventType);
  
  // Format score display
  const scoreDisplay = (homeScore !== undefined && awayScore !== undefined)
    ? `${awayScore} - ${homeScore}`
    : '';

  // Format time display
  const timeDisplay = time || (quarter ? `Q${quarter}` : '');

  return (
    <div className="flex items-start gap-3 p-3 bg-surface-highlight/50 rounded-lg hover:bg-surface-highlight/70 transition-colors">
      {/* Time and score column */}
      <div className="flex-shrink-0 w-20 text-center">
        <div className="text-xs text-gray-400 font-mono">{timeDisplay}</div>
        {scoreDisplay && (
          <div className="text-sm font-bold text-white mt-1">{scoreDisplay}</div>
        )}
      </div>
      
      {/* Icon column */}
      <div className="flex-shrink-0 w-8 text-center text-xl">
        {icon}
      </div>
      
      {/* Description column */}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-200 break-words">
          {description}
        </div>
        {/* Show team/player info if available */}
        {(team || player) && (
          <div className="text-xs text-gray-500 mt-1">
            {team && <span className="mr-2">{team}</span>}
            {player && <span>{player}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayByPlayPanel;
